// Color Palette
export const colors = {
  // Primary colors
  primary: '#006400',
  primaryHover: '#004d00',
  primaryLight: '#008000',

  // Status colors
  success: '#28a745',
  successHover: '#218838',
  danger: '#dc3545',
  dangerHover: '#c82333',
  warning: '#ffc107',
  warningHover: '#e0a800',
  info: '#17a2b8',
  infoHover: '#138496',

  // Neutral colors
  secondary: '#6c757d',
  secondaryHover: '#5a6268',
  light: '#f8f9fa',
  lightHover: '#e2e6ea',
  dark: '#343a40',
  white: '#ffffff',
  black: '#000000',

  // Borders and backgrounds
  border: '#dee2e6',
  borderLight: '#e9ecef',
  backgroundGray: '#f4f6f9',
  backgroundLight: '#fafbfc',

  // Text colors
  textPrimary: '#212529',
  textSecondary: '#6c757d',
  textMuted: '#999999',
  textLight: '#ffffff',
};

// Button Styles
export const buttonStyles = {
  base: {
    padding: '10px 20px',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    outline: 'none',
    display: 'inline-block',
    textAlign: 'center',
    lineHeight: '1.5',
    userSelect: 'none',
  },
  primary: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  primaryHover: {
    backgroundColor: colors.primaryHover,
  },
  secondary: {
    backgroundColor: colors.secondary,
    color: colors.white,
  },
  secondaryHover: {
    backgroundColor: colors.secondaryHover,
  },
  danger: {
    backgroundColor: colors.danger,
    color: colors.white,
  },
  dangerHover: {
    backgroundColor: colors.dangerHover,
  },
  success: {
    backgroundColor: colors.success,
    color: colors.white,
  },
  successHover: {
    backgroundColor: colors.successHover,
  },
  outline: {
    backgroundColor: 'transparent',
    border: `1px solid ${colors.primary}`,
    color: colors.primary,
  },
  outlineHover: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  disabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  small: {
    padding: '5px 15px',
    fontSize: '12px',
  },
  large: {
    padding: '12px 28px',
    fontSize: '16px',
  },
};

// Input Styles
export const inputStyles = {
  base: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    outline: 'none',
    transition: 'border-color 0.2s ease',
    backgroundColor: colors.white,
    color: colors.textPrimary,
  },
  focus: {
    borderColor: colors.primary,
    boxShadow: `0 0 0 3px rgba(0, 100, 0, 0.1)`,
  },
  error: {
    borderColor: colors.danger,
  },
  errorFocus: {
    boxShadow: `0 0 0 3px rgba(220, 53, 69, 0.1)`,
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: '500',
    color: colors.textPrimary,
  },
  errorText: {
    color: colors.danger,
    fontSize: '12px',
    marginTop: '4px',
  },
  formGroup: {
    marginBottom: '16px',
  },
};

// Card Styles
export const cardStyles = {
  base: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    padding: '20px',
    marginBottom: '20px',
  },
  header: {
    borderBottom: `1px solid ${colors.borderLight}`,
    paddingBottom: '12px',
    marginBottom: '16px',
    fontSize: '18px',
    fontWeight: '600',
    color: colors.textPrimary,
  },
  body: {
    fontSize: '14px',
    color: colors.textSecondary,
  },
};

// Table Styles
export const tableStyles = {
  container: {
    width: '100%',
    overflowX: 'auto',
    backgroundColor: colors.white,
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  thead: {
    backgroundColor: colors.backgroundGray,
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: '600',
    color: colors.textPrimary,
    borderBottom: `2px solid ${colors.border}`,
    whiteSpace: 'nowrap',
  },
  tbody: {},
  tr: {
    borderBottom: `1px solid ${colors.borderLight}`,
    transition: 'background-color 0.2s ease',
  },
  trHover: {
    backgroundColor: colors.backgroundLight,
    cursor: 'pointer',
  },
  td: {
    padding: '12px 16px',
    color: colors.textSecondary,
  },
  empty: {
    textAlign: 'center',
    padding: '40px 20px',
    color: colors.textMuted,
    fontSize: '16px',
  },
};

// Modal Styles
export const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  header: {
    padding: '20px 24px',
    borderBottom: `1px solid ${colors.borderLight}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: colors.textPrimary,
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: colors.textSecondary,
    cursor: 'pointer',
    padding: '0',
    lineHeight: '1',
    transition: 'color 0.2s ease',
  },
  closeButtonHover: {
    color: colors.textPrimary,
  },
  body: {
    padding: '24px',
  },
  footer: {
    padding: '16px 24px',
    borderTop: `1px solid ${colors.borderLight}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  },
};

// Pagination Styles
export const paginationStyles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '20px 0',
  },
  button: {
    padding: '8px 12px',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    backgroundColor: colors.white,
    color: colors.textPrimary,
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s ease',
  },
  buttonHover: {
    backgroundColor: colors.backgroundGray,
    borderColor: colors.primary,
    color: colors.primary,
  },
  buttonActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
    color: colors.white,
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  info: {
    fontSize: '14px',
    color: colors.textSecondary,
    padding: '0 16px',
  },
};

// Search Bar Styles
export const searchBarStyles = {
  container: {
    position: 'relative',
    width: '100%',
    maxWidth: '400px',
  },
  input: {
    width: '100%',
    padding: '10px 40px 10px 12px',
    fontSize: '14px',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    outline: 'none',
    transition: 'border-color 0.2s ease',
  },
  inputFocus: {
    borderColor: colors.primary,
    boxShadow: `0 0 0 3px rgba(0, 100, 0, 0.1)`,
  },
  icon: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: colors.textMuted,
    pointerEvents: 'none',
  },
};

// Layout Styles
export const layoutStyles = {
  container: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: colors.backgroundGray,
  },
  sidebar: {
    width: '250px',
    backgroundColor: colors.dark,
    color: colors.white,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)',
    position: 'fixed',
    height: '100vh',
    left: 0,
    top: 0,
    zIndex: 999,
    transition: 'transform 0.3s ease',
  },
  sidebarMobile: {
    transform: 'translateX(-100%)',
  },
  sidebarOpen: {
    transform: 'translateX(0)',
  },
  sidebarHeader: {
    padding: '20px',
    borderBottom: `1px solid rgba(255, 255, 255, 0.1)`,
    fontSize: '20px',
    fontWeight: '700',
    color: colors.white,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sidebarMenu: {
    listStyle: 'none',
    padding: '20px 0',
    margin: 0,
    flex: 1,
    overflowY: 'auto',
  },
  menuItem: {
    padding: '12px 20px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    fontSize: '14px',
    color: colors.white,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    textDecoration: 'none',
  },
  menuItemHover: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  menuItemActive: {
    backgroundColor: colors.primary,
    borderLeft: `4px solid ${colors.white}`,
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '250px',
    transition: 'margin-left 0.3s ease',
  },
  mainContentMobile: {
    marginLeft: 0,
  },
  header: {
    backgroundColor: colors.white,
    padding: '16px 32px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '12px',
  },
  headerTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: colors.textPrimary,
    margin: 0,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },
  content: {
    padding: '32px',
    flex: 1,
    overflow: 'auto',
  },
  hamburgerButton: {
    display: 'none',
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '8px',
    color: colors.textPrimary,
    marginRight: '12px',
  },
  overlay: {
    display: 'none',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 998,
  },
  overlayVisible: {
    display: 'block',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: colors.white,
    fontSize: '24px',
    cursor: 'pointer',
    padding: '4px',
  },
};

// Badge/Status Styles
export const badgeStyles = {
  base: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  success: {
    backgroundColor: colors.success,
    color: colors.white,
  },
  danger: {
    backgroundColor: colors.danger,
    color: colors.white,
  },
  warning: {
    backgroundColor: colors.warning,
    color: colors.dark,
  },
  info: {
    backgroundColor: colors.info,
    color: colors.white,
  },
  secondary: {
    backgroundColor: colors.secondary,
    color: colors.white,
  },
};

// Loading Spinner Styles
export const loadingStyles = {
  overlay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: `4px solid ${colors.borderLight}`,
    borderTop: `4px solid ${colors.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  text: {
    marginTop: '12px',
    fontSize: '14px',
    color: colors.textMuted,
  },
};

// Utility Styles
export const utilityStyles = {
  flexRow: {
    display: 'flex',
    flexDirection: 'row',
  },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  spaceBetween: {
    justifyContent: 'space-between',
  },
  alignCenter: {
    alignItems: 'center',
  },
  gap8: { gap: '8px' },
  gap12: { gap: '12px' },
  gap16: { gap: '16px' },
  gap20: { gap: '20px' },
  marginBottom16: { marginBottom: '16px' },
  marginBottom20: { marginBottom: '20px' },
  marginBottom24: { marginBottom: '24px' },
};

// Responsive Breakpoints
export const breakpoints = {
  mobile: 768,
  tablet: 1024,
  desktop: 1280,
};

// Helper function to check if screen is mobile
export const isMobile = () => {
  return typeof window !== 'undefined' && window.innerWidth < breakpoints.mobile;
};

// Export all as default object
export default {
  colors,
  buttonStyles,
  inputStyles,
  cardStyles,
  tableStyles,
  modalStyles,
  paginationStyles,
  searchBarStyles,
  layoutStyles,
  badgeStyles,
  loadingStyles,
  utilityStyles,
  breakpoints,
  isMobile,
};
